import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import * as tf from '@tensorflow/tfjs';
import { useHistory, useParams } from 'react-router-dom';
import { Box, Button, Typography, Modal, CircularProgress as MuiCircularProgress, Backdrop, IconButton, Fade, LinearProgress } from '@mui/material';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ScreenRotationIcon from '@mui/icons-material/ScreenRotation';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
// Inline SVG silhouettes (animated, multitenant-colorable)
import {
  FrenteSilhouette,
  TraseroSilhouette,
  PerfilIzquierdoSilhouette,
  PerfilDerechoSilhouette,
  MotorSilhouette,
  VolanteSilhouette
} from './VehicleSilhouettes';
import '../styles/Camera.css';
import { analyzeVehicleDetectionBox } from '../utils/detectionBoxAnalysis';
import { parseDetectionOutputs } from '../utils/detectionOutputParser';
import { logger } from '../utils/logger';
import { loadModelWithCache } from '../utils/modelLoader';
import { useToast } from '../ui/ToastProvider';
import { useTenantTheme } from '../tenant/TenantThemeProvider';
import GuidedHintAnimation from './GuidedHintAnimation';
import {
  BASE_QUALITY_THRESHOLDS,
  QUALITY_WARNING_MARGIN,
  getCaptureRule,
  getQualityThresholds,
  buildFeedbackMessage
} from '../config/captureQuality';

// Thresholds for detection models
const DETECTION_THRESHOLDS = {
  angulos: {
    frente: 0.90,
    trasero: 0.90,
    perfil: 0.90
  },
  vin: 0.90,
  chasis: 0.85
};

// New photo classification system
const PHOTO_CLASSES = {
  FRENTE: { id: 1, name: "frente", display_name: "frente" },
  INVALIDA: { id: null, name: "invalida", display_name: "invalida" },
  PERFIL: { id: 2, name: "perfil", display_name: "perfil" },
  TRASERO: { id: 3, name: "trasero", display_name: "trasero" }
};

const ANGULOS_CLASS_LABELS = {
  1: 'frente',
  2: 'perfil',
  3: 'posterior'
};

const GUIDE_TIPS = {
  '0': 'Alinea la parte frontal completa dentro del marco.',
  '1': 'Mantén visible toda la parte posterior, sin cortes.',
  '2': 'Captura el costado izquierdo completo del vehículo.',
  '3': 'Captura el costado derecho completo del vehículo.',
  '6': 'Incluye todo el tablero: velocímetro, luces de alerta y controles.',
  '7': 'Enfoca el odómetro para que los dígitos del kilometraje sean legibles.',
  '9': 'Acércate al VIN/chasis y evita reflejos para mejorar lectura.',
  '10': 'Asegura buena luz y toma el documento completo.'
};

const LOW_VEHICLE_CONFIDENCE_THRESHOLD = 0.35;

const MAX_IMAGE_SIZE_BYTES = 10 * 1024 * 1024;

const MAX_RETRIES_PER_PHOTO = 3;

const detectPortrait = () => {
  if (window.matchMedia) {
    return window.matchMedia('(orientation: portrait)').matches;
  }

  const ratio = window.innerHeight / Math.max(window.innerWidth, 1);
  return ratio > 1.05;
};

const isIOSDevice = () => /iPad|iPhone|iPod/.test(navigator.userAgent)
  || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

const mergeUniqueMessages = (...groups) => {
  const seen = new Set();
  const merged = [];

  groups.flat().filter(Boolean).forEach((message) => {
    if (!seen.has(message)) {
      seen.add(message);
      merged.push(message);
    }
  });

  return merged;
};

const stripGenericFramingMessages = (messages) => {
  const genericPrefixes = [
    'El auto se ve muy pequeño',
    'El auto no se ve completo',
    'Encuadre descentrado',
    'Encuadre demasiado cerrado',
    'No detectamos claramente el vehículo en la imagen'
  ];

  return messages.filter((message) => !genericPrefixes.some((prefix) => message.startsWith(prefix)));
};

const MESSAGE_PRIORITY_RULES = [
  { pattern: 'no detectamos el vehículo con suficiente claridad', priority: 1 },
  { pattern: 'esta foto no coincide con', priority: 2 },
  { pattern: 'la vista no corresponde', priority: 3 },
  { pattern: 'no pudimos validar esta imagen como', priority: 3 },
  { pattern: 'foto borrosa', priority: 4 },
  { pattern: 'imagen muy oscura', priority: 5 },
  { pattern: 'imagen sobreexpuesta', priority: 6 },
  { pattern: 'reflejos excesivos', priority: 7 },
  { pattern: 'estás muy lejos', priority: 8 },
  { pattern: 'falta parte del vehículo', priority: 9 },
  { pattern: 'centra el auto en la imagen', priority: 10 },
  { pattern: 'acércate al vehículo', priority: 11 },
  { pattern: 'acércate un poco más', priority: 12 },
  { pattern: 'validación ajustada', priority: 13 }
];

const getMessagePriority = (message) => {
  const normalized = String(message || '').trim().toLowerCase();
  const matched = MESSAGE_PRIORITY_RULES.find((rule) => normalized.includes(rule.pattern));
  return matched ? matched.priority : 100;
};

const prioritizeMessages = (messages, limit = 2) => {
  return mergeUniqueMessages(messages)
    .sort((left, right) => getMessagePriority(left) - getMessagePriority(right))
    .slice(0, limit);
};

const computeLaplacianVariance = (gray, width, height, step = 2) => {
  let count = 0;
  let sum = 0;
  let sumSquares = 0;

  for (let y = 1; y < height - 1; y += step) {
    for (let x = 1; x < width - 1; x += step) {
      const idx = y * width + x;
      const value =
        gray[(y - 1) * width + x] +
        gray[(y + 1) * width + x] +
        gray[y * width + (x - 1)] +
        gray[y * width + (x + 1)] -
        4 * gray[idx];

      sum += value;
      sumSquares += value * value;
      count += 1;
    }
  }

  if (count === 0) return 0;
  const mean = sum / count;
  return sumSquares / count - mean * mean;
};

const computeFramingMetrics = (gray, width, height, step = 2) => {
  const gradientThreshold = 52;
  let minX = width;
  let minY = height;
  let maxX = 0;
  let maxY = 0;
  let edgeCount = 0;

  for (let y = 1; y < height - 1; y += step) {
    for (let x = 1; x < width - 1; x += step) {
      const gx = gray[y * width + (x + 1)] - gray[y * width + (x - 1)];
      const gy = gray[(y + 1) * width + x] - gray[(y - 1) * width + x];
      const magnitude = Math.abs(gx) + Math.abs(gy);

      if (magnitude > gradientThreshold) {
        edgeCount += 1;
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }

  if (edgeCount < 120) {
    return {
      occupancyRatio: 0,
      centerOffsetRatio: 1,
      hasSubject: false,
      edgeCut: false
    };
  }

  const boxWidth = Math.max(1, maxX - minX + 1);
  const boxHeight = Math.max(1, maxY - minY + 1);
  const occupancyRatio = (boxWidth * boxHeight) / (width * height);
  const centerX = minX + boxWidth / 2;
  const centerY = minY + boxHeight / 2;
  const dx = Math.abs(centerX - width / 2) / width;
  const dy = Math.abs(centerY - height / 2) / height;
  const marginX = Math.max(1, Math.round(width * BASE_QUALITY_THRESHOLDS.edgeMarginRatio));
  const marginY = Math.max(1, Math.round(height * BASE_QUALITY_THRESHOLDS.edgeMarginRatio));
  const touchesLeft = minX <= marginX;
  const touchesRight = maxX >= width - marginX;
  const touchesTop = minY <= marginY;
  const touchesBottom = maxY >= height - marginY;
  const touchesBorder = touchesLeft || touchesRight || touchesTop || touchesBottom;
  const edgeCut = touchesBorder && occupancyRatio > 0.85;

  return {
    occupancyRatio,
    centerOffsetRatio: Math.max(dx, dy),
    hasSubject: true,
    edgeCut
  };
};

const analyzeImageMetrics = (imageData) => {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    img.onload = () => {
      try {
        const maxDim = 420;
        const ratio = Math.min(1, maxDim / Math.max(img.width, img.height));
        const width = Math.max(40, Math.round(img.width * ratio));
        const height = Math.max(40, Math.round(img.height * ratio));

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        ctx.drawImage(img, 0, 0, width, height);

        const data = ctx.getImageData(0, 0, width, height).data;
        const gray = new Float32Array(width * height);

        let brightnessSum = 0;
        let brightPixels = 0;
        let saturationSum = 0;
        for (let i = 0, p = 0; i < data.length; i += 4, p += 1) {
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];
          const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b;
          gray[p] = luma;
          brightnessSum += luma;
          if (luma >= 245) brightPixels += 1;
          const maxChannel = Math.max(r, g, b);
          const minChannel = Math.min(r, g, b);
          const saturation = maxChannel === 0 ? 0 : (maxChannel - minChannel) / maxChannel;
          saturationSum += saturation;
        }

        const pixelCount = width * height;
        const brightness = brightnessSum / pixelCount;
        const glareRatio = brightPixels / pixelCount;
        const blurVariance = computeLaplacianVariance(gray, width, height);
        const framing = computeFramingMetrics(gray, width, height);
        let variance = 0;
        for (let i = 0; i < gray.length; i += 1) {
          const delta = gray[i] - brightness;
          variance += delta * delta;
        }
        const contrastStdDev = Math.sqrt(variance / gray.length);

        resolve({
          width,
          height,
          imageAspectRatio: width / Math.max(height, 1),
          brightness,
          glareRatio,
          blurVariance,
          contrastStdDev,
          avgSaturation: saturationSum / pixelCount,
          ...framing
        });
      } catch (error) {
        reject(error);
      }
    };
    img.onerror = reject;
    img.src = imageData;
  });
};

const sanitizeImageFile = (file) => {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const img = new window.Image();

    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        const maxDim = 2000;
        let { width, height } = img;
        if (width > maxDim || height > maxDim) {
          const ratio = Math.min(maxDim / width, maxDim / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }

        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);

        const cleaned = canvas.toDataURL('image/jpeg', 0.9);
        URL.revokeObjectURL(objectUrl);
        resolve(cleaned);
      } catch (error) {
        URL.revokeObjectURL(objectUrl);
        reject(error);
      }
    };

    img.onerror = (error) => {
      URL.revokeObjectURL(objectUrl);
      reject(error);
    };

    img.src = objectUrl;
  });
};

const Camera = ({ onSavePhoto, fotosTomadas, totalFotosRequeridas }) => {
  const { showError } = useToast();
  const { brandColors } = useTenantTheme();
  const { photoType } = useParams();
  const [image, setImage] = useState(null);
  const [open, setOpen] = useState(false);
  const [qualityMessages, setQualityMessages] = useState([]);
  const history = useHistory();
  const fileInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  const [processingPhoto, setProcessingPhoto] = useState(false);
  const [inspeccionCompletaModalOpen, setInspeccionCompletaModalOpen] = useState(false);
  const [validationResultState, setValidationResultState] = useState('invalid');
  const [pendingCapture, setPendingCapture] = useState(null);
  const [modelLoading, setModelLoading] = useState(false);
  const [modelProgress, setModelProgress] = useState(0);
  const [model, setModel] = useState(null);
  const [modelError, setModelError] = useState(null);
  const [modelLoadSource, setModelLoadSource] = useState('network');
  const [isPortrait, setIsPortrait] = useState(detectPortrait());
  const [dashboardWaitSecondsLeft, setDashboardWaitSecondsLeft] = useState(15);
  const [dashboardWaitRunning, setDashboardWaitRunning] = useState(false);
  const [dashboardReady, setDashboardReady] = useState(false);
  const [preCaptureChecklistDone, setPreCaptureChecklistDone] = useState(false);
  const [captureReady, setCaptureReady] = useState(false);
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [showRetryFallback, setShowRetryFallback] = useState(false);
  const dashboardTimerRef = useRef(null);
  const isIOS = useMemo(() => isIOSDevice(), []);

  // 1. Configuration & Helpers
  const photoConfig = useMemo(() => {
    const config = {
      '0': { model: 'angulos', class: 'Frente', SilhouetteComponent: FrenteSilhouette, classNumber: 1, expectedClass: PHOTO_CLASSES.FRENTE, isVehicleExterior: true },
      '1': { model: 'angulos', class: 'Vista posterior', thresholdKey: 'trasero', SilhouetteComponent: TraseroSilhouette, classNumber: PHOTO_CLASSES.TRASERO.id, expectedClass: PHOTO_CLASSES.TRASERO, isVehicleExterior: true },
      '2': { model: 'angulos', class: 'Perfil Izquierdo', SilhouetteComponent: PerfilIzquierdoSilhouette, classNumber: PHOTO_CLASSES.PERFIL.id, expectedClass: PHOTO_CLASSES.PERFIL, isVehicleExterior: true },
      '3': { model: 'angulos', class: 'Perfil Derecho', SilhouetteComponent: PerfilDerechoSilhouette, classNumber: PHOTO_CLASSES.PERFIL.id, expectedClass: PHOTO_CLASSES.PERFIL, isVehicleExterior: true },
      '4': { model: null, class: 'Motor del Vehículo', SilhouetteComponent: MotorSilhouette },
      '5': { model: null, class: 'Volante del Vehículo', SilhouetteComponent: VolanteSilhouette },
      '6': { model: null, class: 'Panel de Instrumentos', classNumber: 1 },
      '7': { model: null, class: 'Odómetro', classNumber: 2 },
      '8': { model: null, class: 'Rueda de Repuesto' },
      '9': { model: null, class: 'Número de Chasis', classNumber: 1, expectedClass: null },
      '10': { model: null, class: 'Documento del Vehículo' },
    };
    return config[photoType.toString()] || { model: null, class: 'Otra Foto del Vehículo' };
  }, [photoType]);

  const captureRule = useMemo(() => getCaptureRule(photoType), [photoType]);
  const isDashboardPhoto = photoType === '6';
  const requiresPrepChecklist = photoType === '9' || photoType === '10';
  const dashboardWaitProgress = ((15 - dashboardWaitSecondsLeft) / 15) * 100;

  const preCaptureGuide = useMemo(() => {
    if (photoType === '9') {
      return {
        title: 'Preparación previa - número de chasis',
        steps: [
          'Limpia la zona del VIN/chasis para quitar polvo o grasa.',
          'Acércate y encuadra solo el código, sin cortar caracteres.',
          'Evita reflejos directos (linterna o flash frente al código).'
        ]
      };
    }

    if (photoType === '10') {
      return {
        title: 'Preparación previa - documento',
        steps: [
          'Coloca el documento sobre una superficie plana y bien iluminada.',
          'Asegura que se vean los 4 bordes completos en la imagen.',
          'Evita sombras de la mano y reflejos sobre el papel/plástico.'
        ]
      };
    }

    return {
      title: '',
      steps: []
    };
  }, [photoType]);
  const prepAnimationVariant = photoType === '9' ? 'chasis' : 'document';
  const microGuide = useMemo(() => {
    const guides = {
      '0': {
        title: 'Guía rápida - frente',
        steps: [
          'Muestra el vehículo completo dentro del encuadre.',
          'Evita contraluz fuerte y mantén el teléfono estable.'
        ],
        variant: 'exterior'
      },
      '1': {
        title: 'Guía rápida - vista posterior',
        steps: [
          'Incluye toda la zona posterior, sin cortes de esquinas.',
          'Centra la carrocería y evita reflejos intensos.'
        ],
        variant: 'exterior'
      },
      '2': {
        title: 'Guía rápida - perfil izquierdo',
        steps: [
          'Captura todo el lateral izquierdo de punta a punta.',
          'Alinea el vehículo paralelo al encuadre.'
        ],
        variant: 'exterior'
      },
      '3': {
        title: 'Guía rápida - perfil derecho',
        steps: [
          'Captura todo el lateral derecho de punta a punta.',
          'Alinea el vehículo paralelo al encuadre.'
        ],
        variant: 'exterior'
      },
      '4': {
        title: 'Guía rápida - motor',
        steps: [
          'Abre el capó y enfoca la zona principal del motor.',
          'Evita sombras duras y áreas fuera de foco.'
        ],
        variant: 'mechanical'
      },
      '5': {
        title: 'Guía rápida - volante',
        steps: [
          'Centra volante y parte del tablero en la misma toma.',
          'Evita movimientos para mantener nitidez.'
        ],
        variant: 'interior'
      },
      '8': {
        title: 'Guía rápida - rueda de repuesto',
        steps: [
          'Muestra la rueda completa y su zona de guardado.',
          'Asegura buena luz para ver estado y dibujo de la llanta.'
        ],
        variant: 'wheel'
      }
    };

    return guides[photoType] || null;
  }, [photoType]);

  const dashboardGuide = useMemo(() => {
    if (photoType === '6') {
      return {
        title: 'Guía rápida - panel completo',
        steps: [
          'Captura todo el tablero de instrumentos en una sola foto panorámica.',
          'Incluye velocímetro, luces de alerta y controles visibles.',
          'Evita reflejos del parabrisas sobre los indicadores.'
        ],
        variant: 'interior'
      };
    }
    if (photoType === '7') {
      return {
        title: 'Guía rápida - odómetro',
        steps: [
          'Enfoca solo la zona del odómetro/kilometraje.',
          'Acércate lo suficiente para que los dígitos sean legibles.',
          'Evita reflejos y mantén el teléfono estable.'
        ],
        variant: 'interior'
      };
    }
    return null;
  }, [photoType]);

  const requestFullscreenSafe = useCallback(() => {
    const elem = document.documentElement;
    if (
      document.fullscreenElement ||
      document.webkitFullscreenElement ||
      document.mozFullScreenElement ||
      document.msFullscreenElement
    ) {
      return;
    }

    try {
      if (elem.requestFullscreen) {
        elem.requestFullscreen().catch(() => {});
      } else if (elem.webkitRequestFullscreen) {
        elem.webkitRequestFullscreen();
      } else if (elem.msRequestFullscreen) {
        elem.msRequestFullscreen();
      } else if (elem.mozRequestFullScreen) {
        elem.mozRequestFullScreen();
      }
    } catch (_error) {
      // Ignorar: algunos navegadores bloquean fullscreen fuera de gestos válidos.
    }
  }, []);

  useEffect(() => {
    if (isDashboardPhoto) {
      setDashboardWaitSecondsLeft(15);
      setDashboardWaitRunning(false);
      setDashboardReady(false);
      return;
    }

    setDashboardReady(true);
    setDashboardWaitRunning(false);
  }, [isDashboardPhoto]);

  useEffect(() => {
    if (requiresPrepChecklist) {
      setPreCaptureChecklistDone(false);
      return;
    }

    setPreCaptureChecklistDone(true);
  }, [requiresPrepChecklist, photoType]);

  useEffect(() => {
    setCaptureReady(false);
    const timer = window.setTimeout(() => {
      setCaptureReady(true);
    }, 2500);
    return () => window.clearTimeout(timer);
  }, [photoType]);

  useEffect(() => {
    setFailedAttempts(0);
    setShowRetryFallback(false);
  }, [photoType]);

  useEffect(() => {
    if (!dashboardWaitRunning || dashboardReady || !isDashboardPhoto) return undefined;

    dashboardTimerRef.current = window.setInterval(() => {
      setDashboardWaitSecondsLeft((prev) => {
        if (prev <= 1) {
          if (dashboardTimerRef.current) {
            window.clearInterval(dashboardTimerRef.current);
            dashboardTimerRef.current = null;
          }
          setDashboardWaitRunning(false);
          setDashboardReady(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (dashboardTimerRef.current) {
        window.clearInterval(dashboardTimerRef.current);
        dashboardTimerRef.current = null;
      }
    };
  }, [dashboardWaitRunning, dashboardReady, isDashboardPhoto]);

  const analyzeImageQuality = useCallback(async (imageData) => {
    const errors = [];
    const warnings = [];
    const metrics = await analyzeImageMetrics(imageData);
    const thresholds = getQualityThresholds(photoType);
    const isDocumentPhoto = String(photoType) === '10';
    const documentLooksReadable = isDocumentPhoto
      && metrics.contrastStdDev >= 24
      && metrics.blurVariance >= 85;

    if (metrics.brightness < thresholds.minBrightness) {
      errors.push(buildFeedbackMessage('low_light'));
    } else if (metrics.brightness < thresholds.minBrightness + QUALITY_WARNING_MARGIN.brightness) {
      warnings.push('Iluminación justa. Si puedes, usa una zona con más luz.');
    }

    if (metrics.brightness > thresholds.maxBrightness) {
      if (documentLooksReadable) {
        warnings.push('Documento muy claro, pero aún legible. Puedes continuar o recapturar si ves zonas quemadas.');
      } else {
        errors.push(buildFeedbackMessage('over_exposed'));
      }
    } else if (
      metrics.brightness > thresholds.washoutMinBrightness
      && metrics.contrastStdDev < thresholds.washoutMaxContrastStdDev
      && metrics.avgSaturation < thresholds.washoutMaxAvgSaturation
    ) {
      if (documentLooksReadable) {
        warnings.push('Documento muy claro, pero aún legible. Puedes continuar o recapturar si ves zonas quemadas.');
      } else {
        errors.push(buildFeedbackMessage('over_exposed'));
      }
    }

    if (metrics.glareRatio > thresholds.maxGlareRatio) {
      if (documentLooksReadable) {
        warnings.push('Hay reflejos leves, pero la lectura parece suficiente. Recomendado: recapturar solo si faltan datos visibles.');
      } else {
        errors.push(buildFeedbackMessage('glare'));
      }
    }

    if (metrics.blurVariance < thresholds.minBlurVariance) {
      errors.push(buildFeedbackMessage('blur'));
    } else if (metrics.blurVariance < thresholds.minBlurVariance + QUALITY_WARNING_MARGIN.blurVariance) {
      warnings.push('Nitidez al límite. Recomendado: recapturar para mayor precisión.');
    }

    if (captureRule.requireFraming) {
      if (!metrics.hasSubject) {
        errors.push('No detectamos claramente el vehículo en la imagen. Asegúrate de fotografiar el vehículo completo y de que realmente esté dentro del encuadre.');
      } else {
        if (metrics.occupancyRatio < thresholds.minOccupancyRatio) {
          errors.push(buildFeedbackMessage('occupancy_low'));
        } else if (metrics.occupancyRatio < thresholds.minOccupancyRatio + QUALITY_WARNING_MARGIN.occupancyRatio) {
          warnings.push('Encuadre algo lejano. Acércate un poco para mejorar validación.');
        }

        if (metrics.occupancyRatio > thresholds.maxOccupancyRatio) {
          errors.push(buildFeedbackMessage('occupancy_high'));
        }

        if (metrics.edgeCut) {
          errors.push(buildFeedbackMessage('edge_cut'));
        }

        if (metrics.centerOffsetRatio > thresholds.maxCenterOffsetRatio) {
          errors.push(buildFeedbackMessage('off_center'));
        }
      }
    }

    if (captureRule.requireTextReadability) {
      const isChassisPhoto = String(photoType) === '9';
      if (metrics.contrastStdDev < thresholds.minContrastStdDev) {
        if (isChassisPhoto) {
          warnings.push('Legibilidad del chasis dudosa. Recomendado: recapturar con mejor luz, o continuar si el código es visible.');
        } else {
          errors.push(buildFeedbackMessage('text_unreadable'));
        }
      } else if (metrics.contrastStdDev < thresholds.minContrastStdDev + QUALITY_WARNING_MARGIN.contrastStdDev) {
        warnings.push('Lectura probablemente dudosa. Evita reflejos y acércate un poco.');
      }
    }

    logger.debug('[QUALITY] Metrics:', {
      brightness: Number(metrics.brightness.toFixed(2)),
      glarePct: Number((metrics.glareRatio * 100).toFixed(2)),
      blurVariance: Number(metrics.blurVariance.toFixed(2)),
      contrastStdDev: Number(metrics.contrastStdDev.toFixed(2)),
      avgSaturation: Number(metrics.avgSaturation.toFixed(3)),
      occupancyPct: Number((metrics.occupancyRatio * 100).toFixed(2)),
      centerOffsetRatio: Number(metrics.centerOffsetRatio.toFixed(3))
    });

    return { errors, warnings, metrics };
  }, [captureRule, photoType]);

  // Check camera permissions
  const checkCameraPermissions = useCallback(async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      return false;
    }
    
    try {
      await navigator.mediaDevices.getUserMedia({ video: true });
      return true;
    } catch (err) {
      console.error('Camera permission denied:', err);
      // En iOS, no mostrar alert ya que puede interferir con la interacción
      return false;
    }
  }, []);
  
  // Handle native camera capture - Mejorado para iOS
  const openNativeCamera = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, []);

  const openGallery = useCallback(() => {
    if (galleryInputRef.current) {
      galleryInputRef.current.click();
    }
  }, []);
  
  // 2. Model Logic
  useEffect(() => {
    const loadModel = async () => {
      if (!photoConfig.model) {
        setModel(null);
        setModelLoadSource('network');
        return;
      }
      setModelLoading(true);
      setModelProgress(0);
      setModelError(null);
      try {
        const { model: net, source } = await loadModelWithCache(photoConfig.model, (fraction) => {
          setModelProgress(fraction * 100);
        });
        setModel(net);
        setModelLoadSource(source);
        setModelLoading(false);
      } catch (error) {
        logger.error('Error loading model:', error);
        setModelError(error.message);
        setModelLoading(false);
      }
    };
    loadModel();
  }, [photoConfig.model]);

  const runModelOnImage = useCallback(async (imageData) => {
    if (!model) return null;
    try {
      const imgElement = document.createElement('img');
      imgElement.src = imageData;
      await new Promise((resolve) => { imgElement.onload = resolve; });
      const img = tf.browser.fromPixels(imgElement);
      const resized = tf.image.resizeBilinear(img, [320, 320]);
      const casted = resized.cast('int32');
      const expanded = casted.expandDims(0);
      const rawOutputs = await model.executeAsync(expanded);
      const outputTensors = Array.isArray(rawOutputs) ? rawOutputs : [rawOutputs];
      let scores;
      let classes;
      let parsedDetections = null;
      const modelName = photoConfig.model;
      if (Array.isArray(rawOutputs)) {
        if (modelName === 'angulos') {
          parsedDetections = await parseDetectionOutputs(modelName, rawOutputs);
          scores = parsedDetections?.scores;
          classes = parsedDetections?.classes;
        }

        if (!scores || !classes) {
          if (modelName === 'volante') { scores = await rawOutputs[0].array(); classes = await rawOutputs[2].array(); }
          else if (modelName === 'vin') { scores = await rawOutputs[7].array(); classes = await rawOutputs[0].array(); }
          else if (modelName === 'chasis') { scores = await rawOutputs[1].array(); classes = await rawOutputs[3].array(); }
          else { scores = await rawOutputs[1].array(); classes = await rawOutputs[3].array(); }
        }
      } else if (rawOutputs instanceof tf.Tensor) {
        const data = await rawOutputs.array();
        scores = [data[0].slice(0, 100)];
        classes = [data[0].slice(100)];
      }
      tf.dispose([img, resized, casted, expanded, ...outputTensors]);
      scores = scores.flat();
      classes = classes.flat();
      
      // New validation logic for photo classes
      let highestScore = 0;
      let detectedClassId = null;
      let invalidScore = 0;
      const maxScoreByClass = {};
      
      const invalidClassId = PHOTO_CLASSES.INVALIDA.id;

      // Find highest score for each class
      for (let i = 0; i < classes.length; i++) {
        const classId = classes[i];
        const score = scores[i];
        
        // Check for invalid class only if model actually defines one
        if (invalidClassId !== null && classId === invalidClassId && score > invalidScore) {
          invalidScore = score;
        }

        if (!maxScoreByClass[classId] || score > maxScoreByClass[classId]) {
          maxScoreByClass[classId] = score;
        }
        
        // Check for target class
        if (classId === photoConfig.classNumber && score > highestScore) {
          highestScore = score;
          detectedClassId = classId;
        }
      }
      
      const topClassEntry = Object.entries(maxScoreByClass)
        .sort((a, b) => b[1] - a[1])[0];
      const topClassId = topClassEntry ? Number(topClassEntry[0]) : null;
      const topClassScore = topClassEntry ? topClassEntry[1] : 0;
      const detections = parsedDetections?.detections || [];
      const bestVehicleDetection = detections
        .filter((detection) => [1, 2, 3].includes(detection.classId))
        .sort((a, b) => b.score - a.score)[0] || null;

      // Return validation result
      return {
        score: highestScore,
        detectedClass: detectedClassId,
        invalidScore: invalidScore,
        isInvalid: invalidClassId !== null ? invalidScore > 0.5 : false,
        classScores: maxScoreByClass,
        topClassId,
        topClassLabel: topClassId !== null ? (ANGULOS_CLASS_LABELS[topClassId] || `class_${topClassId}`) : 'desconocida',
        topClassScore,
        detections,
        bestVehicleDetection
      };
    } catch (error) {
      logger.error('Error running model on image:', error);
      return null;
    }
  }, [model, photoConfig]);

  // Process the captured image automatically
  const handleCaptureImage = useCallback(async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setQualityMessages(['Tipo de archivo no permitido. Solo se aceptan imágenes.']);
      setOpen(true);
      showError('Formato inválido. Solo se aceptan imágenes.');
      return;
    }

    if (file.size > MAX_IMAGE_SIZE_BYTES) {
      setQualityMessages(['La imagen excede el límite de 10MB. Intente con una imagen más liviana.']);
      setOpen(true);
      showError('La imagen supera el límite de 10MB.');
      return;
    }

    setProcessingPhoto(true);
    setQualityMessages([]); // Reset messages

    try {
      const capturedImage = await sanitizeImageFile(file);
      setImage(capturedImage); // Set image for modal preview

      // 1. Run quality and AI checks
      const qualityAnalysis = await analyzeImageQuality(capturedImage);
      const modelResult = await runModelOnImage(capturedImage);

      let errorMessages = [...qualityAnalysis.errors];
      let warningMessages = [...qualityAnalysis.warnings];
      let threshold = null;
      let score = null;

      // 2. Check AI model threshold and invalid class detection
      if (model && photoConfig.model && modelResult) {
        score = modelResult.score;
        const detectionFeedback = photoConfig.isVehicleExterior && modelResult.bestVehicleDetection
          ? analyzeVehicleDetectionBox({
            detection: modelResult.bestVehicleDetection,
            detectedView: modelResult.topClassLabel,
            expectedView: photoConfig.thresholdKey || photoConfig.class,
            imageAspectRatio: qualityAnalysis.metrics?.imageAspectRatio,
            classScores: modelResult.classScores
          })
          : null;

        if (modelResult.classScores) {
          const printableScores = Object.entries(modelResult.classScores)
            .sort((a, b) => Number(a[0]) - Number(b[0]))
            .map(([classId, classScore]) => ({
              classId: Number(classId),
              label: ANGULOS_CLASS_LABELS[classId] || `class_${classId}`,
              confidence: Number((classScore * 100).toFixed(2))
            }));
          logger.debug('[AI] Class scores (%):', printableScores);
        }
        if (detectionFeedback) {
          logger.debug('[AI] Box feedback:', detectionFeedback);
        }
        
        // First check if photo is detected as invalid
        if (modelResult.isInvalid) {
          errorMessages.push(`Esta foto no coincide con ${photoConfig.class}. Reencuadra y toma la captura nuevamente.`);
        } else {
          if (detectionFeedback && (detectionFeedback.errors.length > 0 || detectionFeedback.warnings.length > 0)) {
            errorMessages = stripGenericFramingMessages(errorMessages);
            warningMessages = stripGenericFramingMessages(warningMessages);
            errorMessages = mergeUniqueMessages(errorMessages, detectionFeedback.errors);
            warningMessages = mergeUniqueMessages(warningMessages, detectionFeedback.warnings);
          }

          // Check normal threshold validation
          const modelName = photoConfig.model;
          if (DETECTION_THRESHOLDS[modelName]) {
            if (typeof DETECTION_THRESHOLDS[modelName] === 'object') {
              const classKey = photoConfig.thresholdKey || (photoConfig.class
                ? photoConfig.class.toLowerCase().replace(/\s+/g, '').replace('número', '').replace('de', '')
                : '');
              threshold = DETECTION_THRESHOLDS[modelName][classKey] || DETECTION_THRESHOLDS[modelName]['perfil'] || null;
            } else {
              threshold = DETECTION_THRESHOLDS[modelName];
            }
          }
          
          if (threshold !== null) {
            const passed = score !== null && score >= threshold;
            const vehicleClassScores = modelResult.classScores
              ? Object.entries(modelResult.classScores)
                .filter(([classId]) => ['1', '2', '3'].includes(String(classId)))
                .map(([, classScore]) => Number(classScore))
              : [];
            const vehicleNotClearlyPresent = vehicleClassScores.length >= 3
              && vehicleClassScores.every((classScore) => classScore < LOW_VEHICLE_CONFIDENCE_THRESHOLD);
            if (!passed) {
              if (vehicleNotClearlyPresent) {
                errorMessages.push(
                  'No detectamos el vehículo con suficiente claridad. Parece que el vehículo no está presente en la imagen o quedó fuera del encuadre. Toma una foto donde el vehículo se vea completo.'
                );
              } else {
                const detectedLabel = modelResult.topClassLabel || 'desconocida';
                errorMessages.push(
                  `No pudimos validar esta imagen como ${photoConfig.class}. El encuadre actual se parece más a ${detectedLabel}. ` +
                  'Sugerencia: centra el objetivo, evita contraluz y vuelve a capturar.'
                );
              }
            } else if (score < threshold + 0.05) {
              warningMessages.push('Validación ajustada: puedes continuar o recapturar para mayor claridad.');
            }
          }
        }
      }

      errorMessages = prioritizeMessages(errorMessages, 2);
      warningMessages = prioritizeMessages(
        warningMessages.filter((message) => !errorMessages.includes(message)),
        2
      );

      // 3. Decide the next step
      if (errorMessages.length > 0) {
        const newFailedAttempts = failedAttempts + 1;
        setFailedAttempts(newFailedAttempts);
        setValidationResultState('invalid');

        if (newFailedAttempts >= MAX_RETRIES_PER_PHOTO) {
          setShowRetryFallback(true);
          setQualityMessages([
            ...errorMessages,
            `Has intentado ${newFailedAttempts} veces. Puedes continuar con esta foto o volver a intentar.`
          ]);
        } else {
          setQualityMessages(errorMessages);
        }
        setPendingCapture(null);
        setOpen(true);
        setProcessingPhoto(false);
      } else {
        const result = { 
          score, 
          passed: true, 
          class: photoConfig.class,
          detectedClass: modelResult?.detectedClass || null,
          invalidScore: modelResult?.invalidScore || 0,
          topClassLabel: modelResult?.topClassLabel || null,
          topClassScore: modelResult?.topClassScore || 0
        };

        setPendingCapture({ imageData: capturedImage, result });

        if (warningMessages.length > 0) {
          setValidationResultState('warning');
          setQualityMessages(warningMessages);
          setOpen(true);
        } else {
          setFailedAttempts(0);
          setShowRetryFallback(false);
          // Auto-commit for vehicle exterior photos (0-3) when validation passes clean
          const isVehicleExterior = ['0','1','2','3'].includes(photoType.toString());
          if (isVehicleExterior) {
            // Skip the modal — save and navigate directly
            onSavePhoto(parseInt(photoType, 10), capturedImage, result);
            const nuevas = fotosTomadas + 1;
            if (nuevas >= totalFotosRequeridas) {
              setInspeccionCompletaModalOpen(true);
            } else {
              history.push('/checklist');
            }
            setProcessingPhoto(false);
            return; // exit early, skip opening modal
          }
          setValidationResultState('success');
          setQualityMessages(['Foto correcta. Cumple con los criterios de validación.']);
          setOpen(true);
        }

        setProcessingPhoto(false);
      }
    } catch (error) {
      logger.error('Error al leer o sanitizar imagen:', error);
      setQualityMessages(['No fue posible procesar la imagen seleccionada. Intente nuevamente.']);
      setOpen(true);
      setProcessingPhoto(false);
      showError('No fue posible procesar la imagen. Intente nuevamente.');
    } finally {
      window.setTimeout(() => requestFullscreenSafe(), 150);
    }
  }, [
    analyzeImageQuality, 
    runModelOnImage, 
    model, 
    photoConfig, 
    showError,
    onSavePhoto,
    photoType,
    fotosTomadas,
    totalFotosRequeridas,
    history,
    requestFullscreenSafe,
    failedAttempts
  ]);
  
  // Take photo using native camera - Mejorado para iOS
  const takePhoto = useCallback(() => {
    if (isDashboardPhoto && !dashboardReady) {
      showError('Espera 15 segundos para que se estabilice el tablero antes de capturar.');
      return;
    }

    if (requiresPrepChecklist && !preCaptureChecklistDone) {
      showError('Completa la preparación guiada antes de capturar.');
      return;
    }

    // Asegurar que el input esté disponible
    if (fileInputRef.current) {
      requestFullscreenSafe();
      openNativeCamera();
    }
  }, [openNativeCamera, isDashboardPhoto, dashboardReady, requiresPrepChecklist, preCaptureChecklistDone, showError, requestFullscreenSafe]);
  
  // 4. Initial Setup
  useEffect(() => {
    // Ensure we have permissions if possible
    checkCameraPermissions();
  }, [checkCameraPermissions]);

  // 3. User Action Handlers

  const handleRetake = useCallback(() => {
    setImage(null);
    setOpen(false);
    setQualityMessages([]); // Clear all messages on retake
    setPendingCapture(null);
    setShowRetryFallback(false);
  }, []);

  const commitCapture = useCallback(() => {
    if (!pendingCapture) return;

    onSavePhoto(parseInt(photoType, 10), pendingCapture.imageData, pendingCapture.result);
    setOpen(false);
    setPendingCapture(null);
    setFailedAttempts(0);
    setShowRetryFallback(false);

    const nuevasFotosTomadas = fotosTomadas + 1;
    if (nuevasFotosTomadas >= totalFotosRequeridas) {
      setInspeccionCompletaModalOpen(true);
    } else {
      history.push('/checklist');
    }
  }, [pendingCapture, onSavePhoto, photoType, fotosTomadas, totalFotosRequeridas, history]);

  const commitCaptureAfterRetries = useCallback(() => {
    setShowRetryFallback(false);
    setValidationResultState('warning');
    setQualityMessages(['Continuando con validación ajustada después de múltiples intentos.']);
    setOpen(true);
  }, []);

  const handleGoBack = useCallback(() => {
    history.goBack();
  }, [history]);

  const startDashboardWait = useCallback(() => {
    setDashboardWaitSecondsLeft(15);
    setDashboardReady(false);
    setDashboardWaitRunning(true);
  }, []);



  const handleInspeccionCompletaConfirm = useCallback(() => {
    setInspeccionCompletaModalOpen(false);
    history.push('/process-images');
  }, [history]);


  const handleOrientationChange = useCallback(() => {
    setIsPortrait(detectPortrait());
  }, []);

  // 5. Lifecycle Effects
  useEffect(() => {
    handleOrientationChange();
    window.addEventListener('resize', handleOrientationChange);
    return () => window.removeEventListener('resize', handleOrientationChange);
  }, [handleOrientationChange]);
  
  // Estado para controlar la visibilidad del botón de captura
  const showCaptureButton = true;
  const canCapture = !modelLoading
    && !processingPhoto
    && (!isDashboardPhoto || dashboardReady)
    && (!requiresPrepChecklist || preCaptureChecklistDone)
    && captureReady;

  // 6. Component UI Rendering
  return (
    <Box sx={{ height: '100dvh', minHeight: '100vh', width: '100vw', backgroundColor: '#000', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Hidden file input for native camera - Optimizado para iOS */}
      <input 
        type="file" 
        accept="image/*" 
        capture={photoType !== '10' ? 'environment' : undefined}
        ref={fileInputRef}
        style={{ 
          position: 'absolute',
          left: '-9999px',
          opacity: 0,
          pointerEvents: 'none'
        }} 
        onChange={handleCaptureImage}
        onClick={(e) => {
          // Limpiar el valor para permitir seleccionar la misma imagen en iOS
          e.target.value = '';
        }}
      />

      {/* Hidden file input for gallery picker (no capture = opens gallery/file chooser) */}
      <input 
        type="file" 
        accept="image/*"
        ref={galleryInputRef}
        style={{ 
          position: 'absolute',
          left: '-9999px',
          opacity: 0,
          pointerEvents: 'none'
        }} 
        onChange={handleCaptureImage}
        onClick={(e) => {
          e.target.value = '';
        }}
      />
      
      {/* Header */}
      <Box sx={{
        position: 'absolute',
        top: 20,
        left: 20,
        right: 20,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        zIndex: 10
      }}>
        <IconButton onClick={handleGoBack} sx={{ color: '#fff', backgroundColor: 'rgba(0,0,0,0.5)', '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' } }}>
          <ArrowBackIcon />
        </IconButton>
      </Box>

      {/* Main Content Area — Clean centered layout */}
      <Box sx={{ 
        flex: 1, 
        display: 'flex', 
        flexDirection: 'column', 
        justifyContent: { xs: 'flex-start', sm: 'center' }, 
        alignItems: 'center', 
        position: 'relative',
        px: { xs: 1.5, sm: 3 },
        pt: { xs: 9, sm: 6 },
        pb: { xs: 15, sm: 10 },
        zIndex: 5,
        overflow: 'auto',
        gap: { xs: 1.2, sm: 1.8 }
      }}>
        {/* Title + Instruction — always centered */}
        <Typography variant="h5" component="h1" sx={{ fontWeight: 'bold', color: '#fff', mb: 0.5, textAlign: 'center', fontSize: { xs: '1.1rem', sm: '1.5rem' }, lineHeight: 1.15, px: 1 }}>
          {photoConfig.class || 'Foto Adicional'}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            px: 2,
            py: 0.5,
            borderRadius: 999,
            backgroundColor: 'rgba(0,0,0,0.45)',
            border: '1px solid rgba(255,255,255,0.22)',
            maxWidth: { xs: '100%', sm: 400 },
            color: '#fff',
            textAlign: 'center',
            mb: 0.5,
            fontSize: { xs: '0.78rem', sm: '0.875rem' },
            lineHeight: 1.3
          }}
        >
          {captureRule.instruction || GUIDE_TIPS[photoType] || 'Encuadra el objetivo completo y evita desenfoque.'}
        </Typography>

        {/* Guide Card — combines SVG silhouette + tips in one single card */}
        {microGuide && (
          <Box
            sx={{
              width: isPortrait ? 'min(94vw, 480px)' : 'min(70vw, 560px)',
              p: { xs: 1.2, sm: 2 },
              borderRadius: 3,
              bgcolor: 'rgba(0,0,0,0.58)',
              border: `1px solid ${brandColors.primary}55`,
              boxShadow: `0 0 20px ${brandColors.primary}22`,
              display: 'flex',
              flexDirection: isPortrait ? 'column' : 'row',
              alignItems: 'center',
              gap: { xs: 1, sm: 2 }
            }}
          >
            {/* SVG Silhouette inside the card */}
            {photoConfig.SilhouetteComponent && (
              <Box sx={{ flexShrink: 0, alignSelf: { xs: 'center', sm: 'auto' } }}>
                <photoConfig.SilhouetteComponent
                  color={brandColors.primary}
                  size={isPortrait ? 64 : 100}
                  animate={true}
                />
              </Box>
            )}
            {/* Text tips */}
            <Box sx={{ flex: 1, textAlign: { xs: 'center', sm: 'left' }, minWidth: 0 }}>
              <Typography variant="subtitle2" sx={{ color: '#fff', fontWeight: 700, mb: 0.5, fontSize: { xs: '0.86rem', sm: '0.95rem' }, lineHeight: 1.2 }}>
                {microGuide.title}
              </Typography>
              {microGuide.steps.map((step) => (
                <Typography key={step} variant="body2" sx={{ color: '#fff', opacity: 0.88, mb: 0.3, fontSize: { xs: '0.72rem', sm: '0.8rem' }, lineHeight: 1.3 }}>
                  • {step}
                </Typography>
              ))}
            </Box>
          </Box>
        )}

        {/* Fallback: if no microGuide but there IS a silhouette, show it standalone */}
        {!microGuide && photoConfig.SilhouetteComponent && (
          <Box sx={{ mb: 2 }}>
            <photoConfig.SilhouetteComponent
              color={brandColors.primary}
              size={isPortrait ? 100 : 130}
              animate={true}
            />
          </Box>
        )}

        {/* Dashboard preparation panel */}
        {isDashboardPhoto && !dashboardReady && (
          <Box
            sx={{
              mt: 0.5,
              width: isPortrait ? 'min(94vw, 480px)' : 'min(60vw, 500px)',
              p: { xs: 1.2, sm: 1.6 },
              borderRadius: 3,
              bgcolor: 'rgba(0,0,0,0.62)',
              border: `1px solid ${brandColors.primary}88`,
              boxShadow: `0 0 18px ${brandColors.primary}33`,
              backdropFilter: 'blur(2px)'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 0.8 }}>
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: '50%',
                  display: 'grid',
                  placeItems: 'center',
                  bgcolor: `${brandColors.primary}33`,
                  animation: dashboardWaitRunning ? 'dashboardPulse 1.1s ease-in-out infinite' : 'none',
                  '@keyframes dashboardPulse': {
                    '0%': { transform: 'scale(0.95)', opacity: 0.75 },
                    '50%': { transform: 'scale(1.05)', opacity: 1 },
                    '100%': { transform: 'scale(0.95)', opacity: 0.75 }
                  }
                }}
              >
                <HourglassBottomIcon sx={{ color: brandColors.primary, fontSize: 20 }} />
              </Box>
              <Typography variant="subtitle2" sx={{ color: '#fff', fontWeight: 700 }}>
                Preparación de tablero
              </Typography>
            </Box>

              <Typography variant="body2" sx={{ color: '#fff', opacity: 0.92, textAlign: 'left', fontSize: { xs: '0.75rem', sm: '0.875rem' }, lineHeight: 1.35 }}>
                1) Enciende el vehículo <PowerSettingsNewIcon sx={{ fontSize: 14, mb: '-2px' }} /> 2) Espera 15 segundos para que se apaguen testigos transitorios 3) Recién ahí toma la foto.
              </Typography>

            <GuidedHintAnimation variant="dashboard" brandColors={brandColors} />

            <LinearProgress
              variant="determinate"
              value={dashboardWaitProgress}
              sx={{
                mt: 1,
                height: 8,
                borderRadius: 999,
                bgcolor: 'rgba(255,255,255,0.16)',
                '& .MuiLinearProgress-bar': { bgcolor: brandColors.primary }
              }}
            />

            <Box sx={{ mt: 1, display: 'flex', alignItems: { xs: 'stretch', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', gap: 1 }}>
              <Typography variant="caption" sx={{ color: '#fff', opacity: 0.92, fontWeight: 700, textAlign: { xs: 'center', sm: 'left' } }}>
                {dashboardWaitRunning ? `Esperando ${dashboardWaitSecondsLeft}s...` : 'Inicia la espera guiada antes de capturar'}
              </Typography>
              <Button
                size="small"
                variant="contained"
                disabled={dashboardWaitRunning}
                onClick={startDashboardWait}
                sx={{
                  bgcolor: brandColors.primary,
                  color: brandColors.onPrimary,
                  fontWeight: 700,
                  '&:hover': { bgcolor: brandColors.secondary }
                }}
              >
                {dashboardWaitRunning ? 'Esperando...' : 'Iniciar 15 s'}
              </Button>
            </Box>
          </Box>
        )}

        {dashboardGuide && (
          <Box
            sx={{
              mt: 0.5,
              width: isPortrait ? 'min(94vw, 480px)' : 'min(60vw, 500px)',
              p: { xs: 1.2, sm: 1.6 },
              borderRadius: 3,
              bgcolor: 'rgba(0,0,0,0.62)',
              border: `1px solid ${brandColors.primary}88`,
              boxShadow: `0 0 18px ${brandColors.primary}33`,
              textAlign: 'left'
            }}
          >
            <Typography variant="subtitle2" sx={{ color: '#fff', fontWeight: 700, mb: 0.8, fontSize: { xs: '0.86rem', sm: '0.95rem' } }}>
              {dashboardGuide.title}
            </Typography>
            {dashboardGuide.steps.map((step, index) => (
              <Typography key={step} variant="body2" sx={{ color: '#fff', opacity: 0.92, mb: index === dashboardGuide.steps.length - 1 ? 0 : 0.6, fontSize: { xs: '0.75rem', sm: '0.875rem' }, lineHeight: 1.35 }}>
                {index + 1}. {step}
              </Typography>
            ))}
            <GuidedHintAnimation variant={dashboardGuide.variant} brandColors={brandColors} />
          </Box>
        )}

        {/* Pre-capture checklist */}
        {requiresPrepChecklist && !preCaptureChecklistDone && (
          <Box
            sx={{
              mt: 0.5,
              width: isPortrait ? 'min(94vw, 480px)' : 'min(60vw, 500px)',
              p: { xs: 1.2, sm: 1.6 },
              borderRadius: 3,
              bgcolor: 'rgba(0,0,0,0.62)',
              border: `1px solid ${brandColors.primary}88`,
              boxShadow: `0 0 18px ${brandColors.primary}33`,
              textAlign: 'left'
            }}
          >
            <Typography variant="subtitle2" sx={{ color: '#fff', fontWeight: 700, mb: 0.8, fontSize: { xs: '0.86rem', sm: '0.95rem' } }}>
              {preCaptureGuide.title}
            </Typography>
            {preCaptureGuide.steps.map((step, index) => (
              <Typography key={step} variant="body2" sx={{ color: '#fff', opacity: 0.92, mb: index === preCaptureGuide.steps.length - 1 ? 0 : 0.6, fontSize: { xs: '0.75rem', sm: '0.875rem' }, lineHeight: 1.35 }}>
                {index + 1}. {step}
              </Typography>
            ))}

            <GuidedHintAnimation variant={prepAnimationVariant} brandColors={brandColors} />

            <Button
              size="small"
              variant="contained"
              onClick={() => setPreCaptureChecklistDone(true)}
              sx={{
                mt: 1.2,
                bgcolor: brandColors.primary,
                color: brandColors.onPrimary,
                fontWeight: 700,
                '&:hover': { bgcolor: brandColors.secondary }
              }}
            >
              Ya revisé, continuar a captura
            </Button>
          </Box>
        )}

        {requiresPrepChecklist && preCaptureChecklistDone && (
          <Typography
            variant="caption"
            sx={{
              display: 'inline-block',
              mt: 1,
              px: 1.2,
              py: 0.4,
              borderRadius: 999,
              bgcolor: `${brandColors.successGreen}33`,
              border: `1px solid ${brandColors.successGreen}88`,
              color: '#fff'
            }}
          >
            Preparación completada. Ya puedes capturar.
          </Typography>
        )}

      {isPortrait && captureRule.requireFraming && (
          <Box
            aria-hidden="true"
            sx={{
              position: 'absolute',
              width: { xs: '82vw', sm: '64vw', md: '50vw' },
              maxWidth: 560,
              height: { xs: '46vh', sm: '44vh', md: '42vh' },
              maxHeight: 360,
              border: '2px dashed rgba(255,255,255,0.7)',
              borderRadius: 3,
              boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.18)',
              zIndex: 1
            }}
          />
        )}

        {captureRule.requireLandscape && isPortrait && !isIOS && (
          <Box
            sx={{
              mt: 0.5,
              px: 1.2,
              py: 0.75,
              borderRadius: 999,
              bgcolor: 'rgba(0,0,0,0.58)',
              border: `1px solid ${brandColors.primary}66`,
              color: '#fff',
              zIndex: 2,
              textAlign: 'center',
              maxWidth: '90vw'
            }}
          >
            <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.7, fontWeight: 700, lineHeight: 1.3 }}>
              <ScreenRotationIcon sx={{ fontSize: 18, color: brandColors.primary }} />
              Recomendado: gira el telefono a horizontal para un mejor encuadre.
            </Typography>
          </Box>
        )}
      </Box>

      {/* Capture & Gallery Buttons */}
      <Box sx={{
        position: 'absolute',
        bottom: isPortrait ? 16 : '50%',
        right: isPortrait ? 'auto' : 30,
        left: isPortrait ? '50%' : 'auto',
        transform: isPortrait ? 'translateX(-50%)' : 'translateY(50%)',
        zIndex: 10,
        display: 'flex',
        gap: 1.5,
        alignItems: 'center',
        opacity: showCaptureButton ? 1 : 0,
        transition: 'opacity 0.3s ease',
        pointerEvents: showCaptureButton ? 'auto' : 'none'
      }}>
        {/* Gallery Button */}
        <IconButton 
          onClick={() => {
            requestFullscreenSafe();
            openGallery();
          }}
          disabled={!canCapture}
          sx={{ 
            bgcolor: 'rgba(255,255,255,0.15)',
            color: '#fff',
            width: 56, 
            height: 56,
            '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' },
            '&:active': { bgcolor: 'rgba(255,255,255,0.3)' },
            opacity: canCapture ? 1 : 0.5,
            cursor: 'pointer',
            WebkitTapHighlightColor: 'transparent'
          }}
        >
          <PhotoLibraryIcon sx={{ fontSize: 28 }} />
        </IconButton>

        {/* Camera Button */}
        <IconButton 
          onClick={takePhoto}
          disabled={!canCapture}
          sx={{ 
            bgcolor: brandColors.primary,
            color: brandColors.onPrimary,
            width: 72, 
            height: 72,
            '&:hover': { bgcolor: brandColors.secondary },
            '&:active': { bgcolor: brandColors.secondary },
            boxShadow: `0 4px 15px ${brandColors.primary}66`,
            opacity: canCapture ? 1 : 0.5,
            cursor: 'pointer',
            WebkitTapHighlightColor: 'transparent'
          }}
        >
          <CameraAltIcon sx={{ fontSize: 40 }} />
        </IconButton>
      </Box>

      {/* Model Loading Indicator */}
      {modelLoading && (
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '88vw', sm: 360 },
          maxWidth: 360,
          zIndex: 15
        }}>
          <Box
            sx={{
              p: { xs: 2, sm: 2.4 },
              borderRadius: 3,
              color: '#fff',
              textAlign: 'center',
              background: 'rgba(8, 11, 24, 0.82)',
              border: `1px solid ${brandColors.primary}66`,
              boxShadow: `0 0 30px ${brandColors.primary}33`,
              backdropFilter: 'blur(8px)'
            }}
          >
            <Box
              sx={{
                width: 62,
                height: 62,
                borderRadius: '50%',
                mx: 'auto',
                mb: 1.4,
                display: 'grid',
                placeItems: 'center',
                bgcolor: `${brandColors.primary}22`,
                boxShadow: `0 0 22px ${brandColors.primary}44`
              }}
            >
              <AutoAwesomeIcon sx={{ color: brandColors.primary, fontSize: 30 }} />
            </Box>

            <Typography sx={{ fontWeight: 800, fontSize: { xs: '1rem', sm: '1.08rem' } }}>
              Preparando validación inteligente
            </Typography>
            <Typography sx={{ mt: 0.7, opacity: 0.88, fontSize: '0.84rem', lineHeight: 1.35 }}>
              {modelLoadSource === 'indexeddb'
                ? 'Usando modelo guardado en el dispositivo.'
                : 'Descargando el modelo de análisis. Esto puede tardar unos segundos la primera vez.'}
            </Typography>

            <LinearProgress
              variant="determinate"
              value={modelProgress}
              sx={{
                mt: 1.5,
                height: 10,
                borderRadius: 999,
                bgcolor: 'rgba(255,255,255,0.12)',
                '& .MuiLinearProgress-bar': {
                  bgcolor: brandColors.primary,
                  borderRadius: 999
                }
              }}
            />

            <Typography sx={{ mt: 1, fontSize: '0.8rem', fontWeight: 700, letterSpacing: 0.3 }}>
              {Math.max(5, Math.round(modelProgress))}%
            </Typography>
          </Box>
        </Box>
      )}

      {/* Model Error */}
      {modelError && (
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          textAlign: 'center',
          color: '#ff4d4d',
          bgcolor: 'rgba(0,0,0,0.7)',
          p: 2,
          borderRadius: 2,
          zIndex: 15
        }}>
          <Typography>Error al cargar el modelo.</Typography>
          <Typography variant="caption">{modelError}</Typography>
        </Box>
      )}

      {/* Full-screen Processing Indicator */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1, flexDirection: 'column' }}
        open={processingPhoto}
      >
        <MuiCircularProgress color="inherit" size={60} />
        <Typography sx={{ mt: 3, fontSize: '1.2rem' }}>
          Revisando imagen...
        </Typography>
      </Backdrop>

      {/* Review Modal (for errors) */}
      <Modal
        open={open}
        onClose={validationResultState === 'invalid' ? undefined : () => setOpen(false)}
        aria-labelledby="review-photo-modal"
        closeAfterTransition
      >
        <Fade in={open}>
          <Box 
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: { xs: 'calc(100vw - 16px)', sm: 'min(92vw, 640px)' },
              maxWidth: '640px',
              maxHeight: 'min(92dvh, 860px)',
              bgcolor: 'background.paper',
              borderRadius: '16px',
              boxShadow: 24,
              display: 'flex',
              flexDirection: 'column',
              outline: 'none',
              overflow: 'hidden',
              overscrollBehavior: 'contain'
            }}
          >
            <Box sx={{ px: { xs: 2, sm: 3 }, pt: { xs: 2, sm: 2.5 }, pb: 1.5, borderBottom: '1px solid #e5e7eb', position: 'relative', flexShrink: 0 }}>
              {validationResultState !== 'invalid' && (
                <IconButton
                  onClick={() => setOpen(false)}
                  sx={{ position: 'absolute', top: 10, right: 10, color: 'grey.500' }}
                >
                  <CloseIcon />
                </IconButton>
              )}
              <Typography id="review-photo-modal" variant="h6" component="h2" sx={{ textAlign: 'center', fontWeight: 600, pr: validationResultState !== 'invalid' ? 4 : 0 }}>
                {validationResultState === 'success' && 'Foto correcta'}
                {validationResultState === 'warning' && 'Resultado dudoso'}
                {validationResultState === 'invalid' && 'Foto inválida'}
              </Typography>
            </Box>

            <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: { xs: 2, sm: 3 }, py: { xs: 1.5, sm: 2 } }}>
              {image && (
                <Box sx={{
                  mb: 2,
                  borderRadius: '12px',
                  overflow: 'hidden',
                  border: '1px solid #ddd',
                  height: { xs: isPortrait ? '30dvh' : '26dvh', sm: '40dvh' },
                  minHeight: { xs: 180, sm: 240 },
                  maxHeight: { xs: 320, sm: 420 },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: '#f8fafc'
                }}>
                  <img
                    src={image}
                    alt="Captura previa"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      display: 'block'
                    }}
                  />
                </Box>
              )}
              {qualityMessages.length > 0 && (
                <Box sx={{
                  bgcolor: validationResultState === 'success' ? '#E8F5E9' : '#FFF3CD',
                  color: validationResultState === 'success' ? '#1B5E20' : '#856404',
                  p: 2,
                  borderRadius: '8px',
                  border: validationResultState === 'success' ? '1px solid #C8E6C9' : '1px solid #FFEEBA'
                }}>
                  {qualityMessages.map((message, index) => (
                    <Typography key={index} variant="body2" sx={{ display: 'flex', alignItems: 'flex-start', mb: index < qualityMessages.length - 1 ? 1 : 0, lineHeight: 1.45 }}>
                      <ErrorOutlineIcon sx={{ fontSize: '1.2rem', mr: 1, mt: '2px', flexShrink: 0 }} /> {message}
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5, p: { xs: 2, sm: 2.5 }, borderTop: '1px solid #e5e7eb', flexWrap: 'wrap', flexShrink: 0, bgcolor: '#fff' }}>
              <Button
                variant="contained"
                onClick={() => {
                  requestFullscreenSafe();
                  handleRetake();
                }}
                sx={{
                  borderColor: brandColors.primary,
                  bgcolor: brandColors.primary,
                  color: brandColors.onPrimary,
                  fontWeight: 600,
                  py: 1,
                  px: 4,
                  minWidth: { xs: '100%', sm: 180 },
                  '&:hover': { bgcolor: brandColors.secondary, borderColor: brandColors.secondary }
                }}
              >
                Reintentar
              </Button>

              {showRetryFallback && (
                <Button
                  variant="contained"
                  onClick={() => {
                    requestFullscreenSafe();
                    commitCaptureAfterRetries();
                  }}
                  sx={{
                    bgcolor: '#FF9800',
                    color: '#fff',
                    fontWeight: 600,
                    py: 1,
                    px: 4,
                    minWidth: { xs: '100%', sm: 220 },
                    '&:hover': { bgcolor: '#F57C00' }
                  }}
                >
                  Continuar de todos modos
                </Button>
              )}

              {validationResultState !== 'invalid' && !showRetryFallback && (
                <Button
                  variant="contained"
                  onClick={() => {
                    requestFullscreenSafe();
                    commitCapture();
                  }}
                  sx={{
                    bgcolor: brandColors.successGreen,
                    color: '#fff',
                    fontWeight: 600,
                    py: 1,
                    px: 4,
                    minWidth: { xs: '100%', sm: 220 },
                    '&:hover': { bgcolor: '#1B5E20' }
                  }}
                >
                  {validationResultState === 'warning' ? 'Continuar con advertencia' : 'Continuar'}
                </Button>
              )}
            </Box>
          </Box>
        </Fade>
      </Modal>

      {/* Inspection Completed Modal */}
      <Modal
        open={inspeccionCompletaModalOpen}
        onClose={() => setInspeccionCompletaModalOpen(false)}
        aria-labelledby="inspeccion-completa-title"
        closeAfterTransition
      >
        <Fade in={inspeccionCompletaModalOpen}>
          <Box 
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: { xs: '85%', sm: '400px' },
              maxWidth: '400px',
              bgcolor: '#ffffff',
              borderRadius: '16px',
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
              p: { xs: 2.5, sm: 3, md: 4 },
              border: '1px solid #e0e0e0'
            }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <CheckCircleOutlineIcon sx={{ color: '#4caf50', fontSize: 56, mb: 2 }} />
              <Typography id="inspeccion-completa-title" variant="h6" component="h2" sx={{ color: '#212529', fontWeight: 600, mb: 2 }}>
                ¡Capturas completadas!
              </Typography>
              <Typography variant="body1" sx={{ color: '#333', mb: 3 }}>
                Has completado todas las fotografías requeridas.
              </Typography>
              <Button
                variant="contained"
                onClick={() => {
                  requestFullscreenSafe();
                  handleInspeccionCompletaConfirm();
                }}
                sx={{ bgcolor: brandColors.primary, color: brandColors.onPrimary, fontWeight: 600, minWidth: '180px', py: 1.2, '&:hover': { bgcolor: brandColors.secondary } }}
              >
                Continuar
              </Button>
            </Box>
          </Box>
        </Fade>
      </Modal>
    </Box>
  );
};

export default Camera;
